import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { StarIcon, MapPinIcon, ClockIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { ArrowUpOnSquareIcon, HeartIcon, StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl } from '../../utils/formatters';
import { businessAPI } from '../../api/businesses';

const FeaturedBusinesses = ({ businesses }) => {
  const navigate = useNavigate();
  const [favById, setFavById] = useState({});

  useEffect(() => {
    const next = {};
    for (const b of businesses || []) {
      next[b.id] = !!b.is_favorite;
    }
    setFavById(next);
  }, [businesses]);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={i} className="h-4 w-4 text-yellow-400" />);
    }
    if (hasHalfStar) {
      stars.push(<StarIcon key="half" className="h-4 w-4 text-yellow-400" />);
    }
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarOutlineIcon key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }
    return stars;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Featured Businesses</h2>
        <button 
          onClick={() => document.getElementById('all-businesses')?.scrollIntoView({ behavior: 'smooth' })}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
        >
          View All →
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {businesses.map((business) => (
          <div
            key={business.id}
            onClick={() => navigate(`/business/${business.id}/book`)}
            className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
          >
            <div className="h-48 relative overflow-hidden">
              {business.business_profile_image ? (
                <img
                  src={resolveImageUrl(business.business_profile_image)}
                  alt={business.business_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-r from-primary-400 to-primary-600 flex items-center justify-center">
                  <span className="text-6xl">
                    {business.business_type === 'salon' && '💇'}
                    {business.business_type === 'clinic' && '🏥'}
                    {business.business_type === 'gym' && '💪'}
                    {business.business_type === 'ride' && '🚗'}
                  </span>
                </div>
              )}

              <div className="absolute top-2 right-2 flex items-center gap-2">
                <button
                  type="button"
                  className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-1.5 shadow"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const shareUrl = `${window.location.origin}/business/${business.id}/book`;
                      if (navigator.share) {
                        await navigator.share({ title: business.business_name, url: shareUrl });
                      } else if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(shareUrl);
                        toast.success('Link copied');
                      } else {
                        window.prompt('Copy this link:', shareUrl);
                      }
                    } catch (err) {
                      toast.error('Failed to share');
                    }
                  }}
                >
                  <ArrowUpOnSquareIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-1.5 shadow"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const current = !!favById[business.id];
                    const next = !current;
                    setFavById((prev) => ({ ...prev, [business.id]: next }));
                    try {
                      if (next) {
                        await businessAPI.addFavorite(business.id);
                        toast.success('Added to favorites');
                      } else {
                        await businessAPI.removeFavorite(business.id);
                        toast.success('Removed from favorites');
                      }
                    } catch (err) {
                      setFavById((prev) => ({ ...prev, [business.id]: current }));
                    }
                  }}
                >
                  {favById[business.id] ? (
                    <HeartSolidIcon className="h-4 w-4 text-red-600" />
                  ) : (
                    <HeartIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-1">
                {business.business_name}
              </h3>
              <div className="flex items-center mb-2">
                <div className="flex items-center mr-2">
                  {renderStars(business.rating)}
                </div>
                <span className="text-sm text-gray-600">
                  ({business.review_count})
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <MapPinIcon className="h-4 w-4 mr-1" />
                <span className="truncate">{business.address}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm font-medium text-primary-600">
                  {business.price_range}
                </span>
                <button
                  className="px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/business/${business.id}/book`);
                  }}
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedBusinesses;
